/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import styles from './VNConfirmPaymentDialog.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { VNDialog } from '../../VNCustomerFacingDisplay/components/VNDialog'
import { getConfirmPaymentReadyDialogOpen } from '../Selectors'
import { ReactComponent as CreditCardIcon } from '../../assets/icons/credit-card.svg'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import { setConfirmPaymentDialog, setOpenCustomerEnteringTipDialog } from '../ActionCreators'
import { sendCFDScreenNav } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { customHistory } from '../../stores/index'
import useRemoteOrderTotal from '../../hooks/useRemoteOrderTotal'
import { clearRemoteOrderTotal } from '../../actions/orderTotalRemote'
import { clearOrderInProgress, updateOrderInProgress } from '../../actions/order'
import { calculateCartTotals } from '../../utils/orderTotalUtils'
import { getCartItems, getTaxByItem, getTaxRate } from '../../selectors/cart'
import { getTipsEnabled, getUseNormalTaxRounding, getUseTaxClasses, makeGetCurrentMenu } from '../../selectors/menus'
import { getMenuServiceCharge } from '../../selectors/items'
import { setCurrentCart, setCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/ActionCreators'
import useCreateOrderInProgress from '../../hooks/useCreateOrderInProgress'
import { getOrderInProgress } from '../../selectors/order'
import { isTabbed } from '../../utils/orderStates'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import ConfirmModalV2 from '../../components/ConfirmModalV2'
import { ReactComponent as CreditCardIconMobile } from '../../assets/icons/credit-card-mobile.svg'
import { getTipOnCardReader } from '../../selectors/peripheral'
import { getIsOrderTaxExempt, getTaxClasses, getTaxRules } from '../../VNTaxes/Selectors'
import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import { setReopenedTabPaymentsAction } from '../../VNTenders/ActionCreators'

const useStyles = makeStyles((theme) => ({
  centeredCard: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 'fit-content',
  },
}))

export const VNConfirmPaymentDialog = () => {
  const classes = useStyles()

  const dispatch = useDispatch()

  // legacy functionality
  const confirmationText = 'Please confirm guest has all forms of payment ready. Once used, a tender may not be canceled.'
  const getMenu = makeGetCurrentMenu()
  const menu = useSelector((state) => getMenu(state))
  const cartItems = useSelector((state) => getCartItems(state))
  const taxRate = useSelector((state) => getTaxRate(state))
  const taxByItem = useSelector((state) => getTaxByItem(state))
  const menuServiceCharge = useSelector((state) => getMenuServiceCharge(state))
  const tipsEnabled = useSelector((state) => getTipsEnabled(state))
  const E700Tipping = useSelector((state) => getTipOnCardReader(state))
  const [createOrder, orderCreated, noPaymentRequired, remoteOrderTotal] = useCreateOrderInProgress()
  const open = useSelector((state) => getConfirmPaymentReadyDialogOpen(state))
  const deviceMode = useSelector((state) => getDeviceMode(state))
  const [, , resetOrderTotals] = useRemoteOrderTotal()
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const useNormalTaxRounding = useSelector(getUseNormalTaxRounding)
  const taxClasses = useSelector(getTaxClasses)
  const taxRules = useSelector(getTaxRules)
  const useTaxClasses = useSelector(getUseTaxClasses)
  const [canProceed, setCanProceed] = useState(true)
  const [proceedSplitTabOrder, setProceedSplitTabOrder] = useState(false)
  const isMobile = useGetIsMobileScreen()
  const tipSuggestions = remoteOrderTotal?.tipSuggestions ?? menu?.tipSuggestions
  const taxExempt = useSelector(getIsOrderTaxExempt)

  useEffect(() => {
    if (open) {
      setCanProceed(true)
    }
  }, [open])

  const getCardIcon = () => {
    return (
      <div className={classes.centeredCard}>
        <CreditCardIcon />
      </div>
    )
  }

  const prepareAndDispatchCartData = () => {
    const _calculatedCartValues = calculateCartTotals({
      remoteOrderTotal,
      cartItems,
      taxRate,
      taxByItem,
      menuServiceCharge,
      orderInProgress,
      isFinalizingTip: null,
      tenderData: null,
      useNormalTaxRounding,
      useTaxClasses,
      taxClasses,
      taxRules,
      taxExempt,
    })
    const extraData = {
      cartTotals: {
        ..._calculatedCartValues,
        uuid: orderInProgress.uuid,
        remainingTicketBalance: orderInProgress?.remainingTicketBalance,
      },
      tips: {
        enabled: tipsEnabled,
        suggestions: tipSuggestions,
      },
      currentPaymentFlow: CFD_POS_PAYMENT_FLOWS.SPLIT_PAY,
    }

    // store the current cart values in state on the POS side
    dispatch(setCurrentCart(_calculatedCartValues))

    // need to show the tip screen on the CFD
    sendCFDScreenNav(CFD_SCREEN.TRANSACTION_SUMMARY, extraData)

    // need to show the modal on POS showing that the customer is entering the tip
    dispatch(
      setOpenCustomerEnteringTipDialog({ open: true, amount: _calculatedCartValues.total ?? 1122, tipsEnabled: tipsEnabled }),
    )

    setCanProceed(true)
    setProceedSplitTabOrder(false)
  }

  useEffect(() => {
    if ((proceedSplitTabOrder || orderCreated) && deviceMode === MODES.POS) {
      prepareAndDispatchCartData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCreated, proceedSplitTabOrder])

  const primaryButtonClick = () => {
    //if we got into the split flow and the order has an auth stored, move the auth to VNTenders so it can be re applied.
    if (orderInProgress?.payments?.length) {
      //store the auth in VNTenders
      dispatch(setReopenedTabPaymentsAction({ orderUuid: orderInProgress.uuid, payments: orderInProgress?.payments || [] }))
      //remove the auth from the order
      dispatch(updateOrderInProgress({ payments: [] }))
    }

    dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.SPLIT_PAY, true))
    // For the E700 we want to act as if tipping is disabled at the beginning of the split transaction.
    if (tipsEnabled && !E700Tipping) {
      if (deviceMode === MODES.POS) {
        if (canProceed) {
          setCanProceed(false)

          if (isTabbed(orderInProgress)) {
            setProceedSplitTabOrder(true)
            return
          }

          // create an order before allowing tip
          createOrder(true)
        }
      } else {
        dispatch(setConfirmPaymentDialog(false))

        customHistory.push({
          pathname: '/signature',
          state: {
            addTipThenRedirectPath: '/split-tender',
          },
        })
      }
    } else {
      if (deviceMode === MODES.POS) {
        // show waiting for attendant modal on CFD
        sendCFDScreenNav(CFD_SCREEN.SPLIT_PAY_WAITING_FOR_ATTENDANT)

        if (isTabbed(orderInProgress)) {
          setProceedSplitTabOrder(true)
          return
        }

        createOrder(true)
      } else {
        dispatch(setConfirmPaymentDialog(false))
        customHistory.push('/split-tender')
      }
    }
  }

  const secondaryButtonClick = () => {
    dispatch(clearRemoteOrderTotal())
    sendCFDScreenNav(CFD_SCREEN.CART)
    // reset order totals
    resetOrderTotals()
    if (!isTabbed(orderInProgress)) {
      dispatch(clearOrderInProgress())
    }
    dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
    dispatch(setConfirmPaymentDialog(false))
  }

  if (!open) {
    return null
  }

  return (
    <ConfirmModalV2
      headerText={<CreditCardIconMobile className={styles.icon} />}
      subtext={confirmationText}
      buttonOneText={'GO BACK'}
      buttonTwoText={'PROCEED'}
      onButtonOneClick={secondaryButtonClick}
      onButtonTwoClick={primaryButtonClick}
    />
  )
}
